import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, PlayCircle, Info, MousePointerClick } from "lucide-react";
import { CATEGORIES, SCENARIOS } from "./scenarios";
import { WORKFLOW_SCENARIOS } from "./workflow/scripts";
import { DemoBadge } from "./DemoPanels";

/*
 * /promoter/demos: landing page.
 *   1) tiny helper strip explaining what these demos are for
 *   2) search across scenario titles + keywords + subtitles
 *   3) categories grouped grid of scenario cards
 * Selecting a card navigates to /promoter/demos/:scenarioId.
 * All data is fictional; no scenario writes to the real database.
 */

export default function PromoterDemosIndex() {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();

  const matches = useMemo(() => {
    if (!query) return SCENARIOS;
    return SCENARIOS.filter(s => {
      const hay = [
        s.title, s.subtitle, s.category,
        ...(s.keywords || []),
      ].join(" ").toLowerCase();
      return hay.includes(query);
    });
  }, [query]);

  const byCat = useMemo(() => {
    const map = {};
    for (const s of matches) (map[s.category] ||= []).push(s);
    return map;
  }, [matches]);

  const visibleCategories = CATEGORIES.filter(c => (byCat[c.key] || []).length > 0);

  return (
    <div data-testid="promoter-demos-page" className="pb-10">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900">Interactive Demos</h1>
            <DemoBadge>SANDBOX</DemoBadge>
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Pick the exact scenario the manager just asked about and walk them
            through the real ConfirmPH workflow: safely, with fictional data.
          </p>
        </div>
      </div>

      {/* Explainer */}
      <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-xs text-slate-800 flex items-start gap-2">
        <Info className="w-4 h-4 text-emerald-800 flex-shrink-0 mt-0.5" />
        <div>
          Every demo runs in a sandbox: no real bookings, customers, vehicles,
          notifications, or messaging are created. Use these while standing with a
          manager to answer their real-world questions visually.
        </div>
      </div>

      {/* Search */}
      <div className="mt-5 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={e => setQ(e.target.value)}
                data-testid="demos-search"
                placeholder="Search: double booking, messenger, deposit, late return…"
                className="w-full rounded-full border border-slate-300 pl-9 pr-3 py-2 text-sm bg-white" />
      </div>

      {visibleCategories.length === 0 && (
        <div className="mt-6 text-sm text-slate-500" data-testid="demos-empty">
          No scenarios match &ldquo;{q}&rdquo;.
        </div>
      )}

      {/* Categories */}
      <div className="mt-6 space-y-8">
        {visibleCategories.map(cat => (
          <section key={cat.key} data-testid={`demos-cat-${cat.key}`}>
            <div className="mb-2">
              <h2 className="font-semibold text-slate-900 text-sm">{cat.label}</h2>
              <p className="text-xs text-slate-500">{cat.subtitle}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {byCat[cat.key].map(s => (
                <ScenarioCard key={s.id} s={s} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function ScenarioCard({ s }) {
  const interactive = !!WORKFLOW_SCENARIOS[s.id];
  return (
    <Link to={`/promoter/demos/${s.id}`}
          data-testid={`demo-card-${s.id}`}
          className="group rounded-xl border border-slate-200 bg-white p-3 hover:border-emerald-400 hover:shadow-md transition-all">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-800 flex-shrink-0">
          <PlayCircle className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-slate-900 leading-tight">{s.title}</div>
            {interactive && (
              <span data-testid={`badge-interactive-${s.id}`}
                    className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5">
                <MousePointerClick className="w-2.5 h-2.5" /> Interactive
              </span>
            )}
          </div>
          <div className="text-xs text-slate-600 mt-1 leading-relaxed">{s.subtitle}</div>
          <div className="mt-2 flex flex-wrap gap-1">
            {(s.keywords || []).slice(0, 3).map(k => (
              <span key={k} className="text-[10px] rounded-full bg-slate-100 text-slate-700 px-1.5 py-0.5">{k}</span>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
}
